<script lang="ts">
    import { toast, toastIdCounter, visibleToasts } from '$lib/stores';
    import { DateTime } from 'luxon';
    import { onMount } from 'svelte';
    import type { ToastData, ToastOptions } from '$lib/types/toast.js';
    import { fly } from 'svelte/transition';
    import ErrorToastIcon from '$lib/assets/icons/ErrorToastIcon.svelte';
    import WarningToastIcon from '$lib/assets/icons/WarningToastIcon.svelte';
    import SuccessToastIcon from '$lib/assets/icons/SuccessToastIcon.svelte';
    import InfoToastIcon from '$lib/assets/icons/InfoToastIcon.svelte';

    onMount(() => {
        const intervalId = setInterval(updateToasts, 500);
        updateToasts();

        $toast = {
            showToast,
            showSuccess,
            showMessage,
            showInfo,
            showWarning,
            showError
        };

        return () => clearInterval(intervalId);
    });

    function updateToasts() {
        $visibleToasts = $visibleToasts.filter((t) => t.lifetimeEnd.diffNow().milliseconds > 0);
    }

    export function showToast(options: Partial<ToastOptions>): void {
        const defaultOptions: Omit<ToastData, 'lifetimeEnd'> = {
            id: $toastIdCounter++,
            message: '',
            duration: {
                millisecond: 2500
                // millisecond: 999999 // for testing
            },
            alertClass: '',
            icon: undefined,
            iconClass: '',
            created: DateTime.now()
        };

        $visibleToasts = [
            ...$visibleToasts.filter((t) => t.message !== options.message),
            {
                ...defaultOptions,
                ...options,
                lifetimeEnd: DateTime.now().plus(options.duration || defaultOptions.duration)
            }
        ];
    }

    export function showSuccess(message: string): void {
        showToast({
            message,
            alertClass: 'text-[#477785]',
            icon: SuccessToastIcon
        });
    }

    export function showMessage(message: string): void {
        showToast({
            message
        });
    }

    export function showInfo(message: string): void {
        showToast({
            message,
            alertClass: 'text-[#477785]',
            icon: InfoToastIcon
        });
    }

    export function showWarning(message: string): void {
        showToast({
            message,
            alertClass: 'text-[#D87A1B]',
            icon: WarningToastIcon
        });
    }

    export function showError(message: string): void {
        showToast({
            message,
            alertClass: 'text-[#B42133]',
            icon: ErrorToastIcon
        });
    }

    function hideToast(toastId: number) {
        $visibleToasts = $visibleToasts.filter((t) => t.id !== toastId);
    }
</script>

<div class="z-50 flex items-end toast">
    {#each $visibleToasts as toast (toast.id)}
        <div
            transition:fly={{ x: 50, duration: 200 }}
            class="group w-fit relative flex text-base font-jost bg-white gap-2.5 pl-4 pr-5 shadow-lg py-3 font-medium rounded-none {toast.alertClass}"
        >
            {#if toast.icon}
                <svelte:component this={toast.icon} class={toast.iconClass} />
            {/if}

            <span>
                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                {@html toast.message}
            </span>
            <div
                class="group-hover:opacity-100 opacity-0 transition-all duration-150 absolute pr-4 flex justify-end items-center right-0 top-0 h-full bg-gradient-to-r from-white/0 to-white w-1/2"
            >
                <button on:click={() => hideToast(toast.id)}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 10 10" fill="none">
                        <path d="M1 1L9 8.99962" stroke="#BAB3B0" />
                        <path d="M1 9L9 1.00038" stroke="#BAB3B0" />
                    </svg>
                </button>
            </div>
        </div>
    {/each}
</div>
